import type { CommunityScorePercentageType } from "@/components/community-score-wheel";
import type { CategoryType } from "@/components/game-categories";
import type { PaginationDataType } from "@/components/pagination";
import type { CouponType } from "@/components/promotion-panel";

import type { AwardsType } from "../game/types";

export const GameStatusEnum = {
  CS: "CS", // Coming Soon
  OP: "OP", // Open
  PC: "PC", // Permanently Closed
  TC: "TC", // Temporarily Closed
} as const;

export const GameStatusFilterEnum = {
  COMING_SOON: "COMING_SOON",
  OPEN: "OPEN",
  PERMANENTLY_CLOSED: "PERMANENTLY_CLOSED",
  TEMPORARILY_CLOSED: "TEMPORARILY_CLOSED",
} as const;
export type GameStatusFilterEnum =
  (typeof GameStatusFilterEnum)[keyof typeof GameStatusFilterEnum];

export const GameKindEnum = {
  RER: "RER",
  TER: "TER",
  VER: "VER",
} as const;
export type GameKindEnum = (typeof GameKindEnum)[keyof typeof GameKindEnum];

export const OverallRatingEnum = {
  DISLIKE: "DISLIKE",
  LIKE: "LIKE",
  LOVE: "LOVE",
} as const;

export interface AddressType {
  address_components: {
    long_name: string;
    short_name: string;
    types: string[];
  }[];
  formatted_address: string;
}

export interface GameImageType {
  imageId?: string;
  order?: number;
}

export interface SearchGameType {
  __typename: "GameType";
  awards?: AwardsType[];
  closingAt?: string;
  communityRatingCount?: number;
  communityScoreBucket?: string;
  communityScorePercentages?: CommunityScorePercentageType[];
  coupons?: CouponType[];
  description: string;
  difficulty?: string;
  gameimageSet?: GameImageType[];
  gameLocation: {
    company: {
      id: string;
      name: string;
      slug: string;
    };
    displayName?: string;
    id: string;
    slug: string;
  };
  hasAwards: boolean;
  hasBookings: boolean;
  id: string;
  isCompleted?: boolean;
  isPromoted: boolean;
  isSaved?: boolean;
  isScary: boolean;
  kind: GameKindEnum;
  minutes?: number;
  name: string;
  openingAt?: string;
  overallRating?: keyof typeof OverallRatingEnum;
  playersMax: number;
  playersMin: number;
  primaryCategory: CategoryType;
  promotedBidId: string;
  secondaryCategories: CategoryType[];
  slug: string;
  status: keyof typeof GameStatusEnum;
  tags: { id: string }[];
  timeslots: Array<{
    datetime: string;
    available: boolean;
  }>;
}

export const KindEnum = {
  RL_ESCAPE_ROOM: "RL_ESCAPE_ROOM",
  TABLETOP_ESCAPE_ROOM: "TABLETOP_ESCAPE_ROOM",
  VIRTUAL_ESCAPE_ROOM: "VIRTUAL_ESCAPE_ROOM",
} as const;
export type KindEnum = (typeof KindEnum)[keyof typeof KindEnum];

export const SortEnum = {
  COMMUNITY_SCORE: "COMMUNITY_SCORE",
  DATE_PLAYED: "DATE_PLAYED",
  DISTANCE: "DISTANCE",
  MY_RATING: "MY_RATING",
  USER_RATING: "USER_RATING",
  USER_REVIEW: "USER_REVIEW",
  WISHLIST: "WISHLIST",
} as const;
export type SortEnum = (typeof SortEnum)[keyof typeof SortEnum];

export const DifficultyEnum = {
  EASY: "EASY",
  EXPERT: "EXPERT",
  HARD: "HARD",
  MEDIUM: "MEDIUM",
  UNKNOWN: "UNKNOWN",
} as const;
export type DifficultyEnum =
  (typeof DifficultyEnum)[keyof typeof DifficultyEnum];

export const PrivacyEnum = {
  ALWAYS_PRIVATE: "ALWAYS_PRIVATE",
  NEVER_PRIVATE: "NEVER_PRIVATE",
  SOMETIMES_PRIVATE: "SOMETIMES_PRIVATE",
} as const;
export type PrivacyEnum = (typeof PrivacyEnum)[keyof typeof PrivacyEnum];

export const IncludeEnum = {
  ALL: "ALL",
  NOT_PLAYED: "NOT_PLAYED",
  PLAYED: "PLAYED",
  SAVED: "SAVED",
} as const;
export type IncludeEnum = (typeof IncludeEnum)[keyof typeof IncludeEnum];

export interface LocationType {
  __typename: "LocationType";
  company: {
    name: string;
    slug: string;
  };
  displayName?: string;
  distance?: number;
  games?: SearchGameType[];
  id: string;
  locality: {
    locality: string;
    region: string;
  };
  slug: string;
}

export interface MapLocationType {
  __typename: "LocationType";
  company: {
    name: string;
  };
  displayName?: string;
  games: {
    id: string;
    isCompleted: boolean;
    isSaved?: boolean;
    isScary: boolean;
    overallRating?: keyof typeof OverallRatingEnum;
    primaryCategory: CategoryType;
    status: keyof typeof GameStatusEnum;
    tags: { id: string }[];
  }[];
  id: string;
  location?: {
    coordinates: [number, number];
  };
}

export type GameOrLocationType = SearchGameType | LocationType;

export interface DataType {
  games: PaginationDataType<GameOrLocationType>;
}

export interface MapDataType {
  games: PaginationDataType<MapLocationType>;
}

export interface VariablesFiltersType {
  categoryId?: number;
  categoryIds?: number[];
  categorySlugs?: string[];
  difficulty?: DifficultyEnum[];
  hasBookings?: boolean;
  isGoodForKids?: boolean;
  isScary?: boolean;
  kind?: KindEnum[];
  minutesGte?: number;
  minutesLte?: number;
  players?: number;
  privacy?: PrivacyEnum[];
  status?: GameStatusFilterEnum[];
  tags?: string[];
  withCouponsOnly?: boolean;
}

export interface VariablesType {
  boundingBox?: {
    neLat: number;
    neLng: number;
    swLat: number;
    swLng: number;
  };
  client?: string;
  distance?: number;
  escapedOnly?: boolean;
  excludeNotPlayed?: boolean;
  excludePlayed?: boolean;
  excludeSaved?: boolean;
  filters?: VariablesFiltersType;
  groupByLocation?: boolean;
  includeAllGames?: boolean;
  isUser: boolean;
  lat?: number;
  lng?: number;
  page?: number;
  pageSize?: number;
  query?: string;
  savedOnly?: boolean;
  sortBy?: SortEnum;
  userId?: string;
  userIds?: string[];
}
